/**
 * Files attached to the CONTACT — a scanned licence, a signed quote, the
 * clinic's price list.
 *
 * Nothing here is new infrastructure. `ownerType: 'CONTACT'` has been a valid
 * attachment owner since Step 3 and `ListAttachments` has answered for it ever
 * since; what was missing was a screen that asked. So this is the interaction
 * composer's upload flow and the timeline's download button, over the contact
 * instead of over one interaction:
 *
 *   POST /files/initiate  →  PUT straight to storage  →  POST /files/complete
 *
 * The bytes never pass through the BFF, the PUT reports progress because it is
 * an XHR, and a download URL is minted per CLICK rather than rendered as an
 * href — a presigned URL expires in minutes, so a link built at render time is
 * dead by the time anybody uses it.
 *
 * A PENDING row is invisible to every read path, so an abandoned upload leaks
 * a row rather than showing as a broken attachment.
 */
import { Button, Card, EmptyState, Spinner } from '@meshhub/design-tokens/ui';
import { Paperclip, Plus } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { formatBytes, formatDate } from '../../lib/format.js';
import { openAttachment, uploadFile } from '../../lib/files.js';
import { S } from '../../lib/strings.js';
import { MutationError } from '../FormKit.web.jsx';

export function FilesPanel({ contact, onSaved, readOnly }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(null); // { name, progress }
  const [error, setError] = useState(null);

  const files = contact.attachments ?? [];

  async function add(list) {
    setError(null);
    for (const file of [...list]) {
      setUploading({ name: file.name, progress: 0 });
      try {
        await uploadFile({
          file,
          ownerType: 'CONTACT',
          ownerId: contact.id,
          onProgress: (fraction) => setUploading({ name: file.name, progress: fraction }),
        });
      } catch (err) {
        setError(err);
        break;
      } finally {
        setUploading(null);
      }
    }
    // Re-read rather than appending locally: the server decides what is READY,
    // and the panel showing something the server would not list is exactly the
    // divergence the profile's refetch-after-save rule exists to avoid.
    await onSaved?.();
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <Card>
      <div className="mb-4 flex items-center gap-3">
        <h2 className="text-md font-bold">{S.contacts.detail.files}</h2>
        {!readOnly && (
          <>
            <Button
              variant="ghost"
              className="ms-auto"
              onClick={() => inputRef.current?.click()}
              disabled={Boolean(uploading)}
            >
              <Plus size={14} aria-hidden="true" />
              {S.contacts.detail.addFile}
            </Button>
            <input
              ref={inputRef}
              type="file"
              multiple
              className="hidden"
              aria-label={S.contacts.detail.addFile}
              onChange={(event) => event.target.files?.length && add(event.target.files)}
            />
          </>
        )}
      </div>

      <MutationError error={error} />

      {uploading && (
        <p role="status" className="mb-3 flex items-center gap-2 text-sm text-text-muted">
          <Spinner />
          {uploading.name}
          <span className="tabular-nums">{Math.round(uploading.progress * 100)}٪</span>
        </p>
      )}

      {files.length === 0 && !uploading ? (
        <EmptyState title={S.contacts.detail.noFiles} />
      ) : (
        <ul className="flex flex-col gap-2">
          {files.map((file) => (
            <li key={file.id} className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => openAttachment(file.id)}
                title={`${file.fileName} — ${formatBytes(file.sizeBytes)}`}
                className="flex items-center gap-2 rounded border border-border px-3 py-1
                           text-sm text-text-muted hover:bg-surface-hover hover:text-text"
              >
                <Paperclip size={13} aria-hidden="true" />
                {file.fileName}
              </button>
              <span className="text-xs text-text-muted">{formatBytes(file.sizeBytes)}</span>
              {file.createdAt && (
                <span className="ms-auto text-xs text-text-muted">
                  {formatDate(file.createdAt)}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
